import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { MapPin, Package, Clock, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { fmtDate, fmtDateTime } from "@/lib/utils-format";

export default function MobileHome() {
  const [checkins, setCheckins] = useState([]);
  const [deliveries, setDeliveries] = useState([]);

  useEffect(() => {
    api.get("/mobile/checkins?scope=me").then((r) => setCheckins(r.data)).catch(() => {});
    api.get("/mobile/deliveries").then((r) => setDeliveries(r.data)).catch(() => {});
  }, []);

  const lastCheckin = checkins[0];
  const isCheckedIn = lastCheckin?.type === "check_in";

  return (
    <div className="p-4 space-y-4" data-testid="m-home">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-[#8B7355]">Good {new Date().getHours() < 12 ? "morning" : new Date().getHours() < 18 ? "afternoon" : "evening"}</div>
        <h1 className="font-[Outfit] text-2xl font-semibold tracking-tight mt-0.5">Field Dashboard</h1>
      </div>

      {/* Status card */}
      <div className={`rounded-xl p-4 ${isCheckedIn ? "bg-[#F0FDF4] border border-emerald-200" : "bg-[#FEF9EA] border border-amber-200"}`}>
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isCheckedIn ? "bg-emerald-500" : "bg-amber-500"} text-white`}>
            {isCheckedIn ? <CheckCircle2 className="w-5 h-5" /> : <Clock className="w-5 h-5" />}
          </div>
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-widest text-[#5C4033]">Current status</div>
            <div className="font-semibold" data-testid="m-status">
              {isCheckedIn ? "Checked in" : lastCheckin ? "Checked out" : "Not checked in today"}
            </div>
            {lastCheckin && (
              <div className="text-xs text-[#8B7355] mt-0.5">
                {fmtDateTime(lastCheckin.timestamp)} · {lastCheckin.lat?.toFixed(3)}, {lastCheckin.lng?.toFixed(3)}
              </div>
            )}
          </div>
        </div>
        <Link
          to="/m/checkin"
          data-testid="m-home-checkin-cta"
          className="mt-3 flex items-center justify-center gap-2 h-11 rounded-md bg-[#2C1810] text-white text-sm font-semibold uppercase tracking-widest"
        >
          <MapPin className="w-4 h-4" />
          {isCheckedIn ? "Check out" : "Check in now"}
        </Link>
      </div>

      {/* Deliveries preview */}
      <div className="bg-white border border-[#EAE5D9] rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Package className="w-4 h-4 text-[#C05621]" />
            <div className="font-semibold text-sm">Assigned deliveries</div>
          </div>
          <Link to="/m/deliveries" className="text-xs text-[#C05621] hover:underline" data-testid="m-home-see-all">See all</Link>
        </div>
        {deliveries.length === 0 ? (
          <div className="text-center py-6 text-[#8B7355] text-sm">
            <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
            No deliveries assigned to you yet.
          </div>
        ) : (
          <ul className="space-y-2">
            {deliveries.slice(0, 3).map((d) => (
              <li key={d.id}>
                <Link
                  to={`/m/deliveries/${d.id}`}
                  data-testid={`m-home-delivery-${d.id}`}
                  className="flex items-center justify-between p-3 rounded-md hover:bg-[#FDFBF7] border border-[#EAE5D9]"
                >
                  <div>
                    <div className="font-medium text-sm">{d.number}</div>
                    <div className="text-xs text-[#8B7355]">{d.customer_snapshot?.name}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold">₹{Math.round(d.grand_total || 0).toLocaleString("en-IN")}</div>
                    <div className="text-[10px] uppercase tracking-widest text-[#C05621]">{d.doc_type}</div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Recent activity */}
      <div className="bg-white border border-[#EAE5D9] rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-4 h-4 text-[#C05621]" />
          <div className="font-semibold text-sm">Recent check-ins</div>
        </div>
        {checkins.length === 0 ? (
          <div className="text-center py-4 text-[#8B7355] text-sm">No check-ins yet.</div>
        ) : (
          <ul className="space-y-2">
            {checkins.slice(0, 5).map((c) => (
              <li key={c.id} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${c.type === "check_in" ? "bg-emerald-500" : "bg-amber-500"}`}></div>
                  <span className="capitalize">{c.type.replace("_", " ")}</span>
                </div>
                <span className="text-[#8B7355] text-xs">{fmtDateTime(c.timestamp)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
